import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "@transfr-inc/dashboard-components";
import React, { useEffect, useMemo, useState } from "react";
import SyncIntegrationErrorsModal from "@components/modals/sync-integration-errors/sync-integration-errors.modal";
import { useContainer } from "@src/contexts/container-context";
import { useApiRequest } from "@lib/hooks";
import { useCurrentUser } from "@queries";
import { useNotifications } from "@views/integrations/hooks";
import {
  getCacheKey,
  getCachedSyncErrors,
  setCachedSyncErrors,
} from "@views/integrations/utils/sync-errors-cache";

import "./sync-badge.scss";

/**
 * Component that displays the sync status badge for an integration connection.
 *
 * This component shows different states:
 * - Loading: Skeleton loader while fetching sync errors
 * - Error: Badge with "View Errors" that opens a modal with error details
 * - Success: Green badge indicating successful sync
 *
 * The component fetches sync errors from the API (with caching) to determine
 * if a "Success" status actually has errors that should be displayed.
 *
 * @param {string|number} connectionId - Connection ID
 * @param {string|number} jobId - Job execution ID
 * @param {string} runStatus - Status of the last run ("Success", "Failed", etc.)
 * @param {Function} setFailed - Callback to notify parent of error state
 * @param {string} organizationCode - Organization code (optional, falls back to user's org)
 */
const SyncBadge = ({
  connectionId,
  jobId,
  runStatus = "Failed",
  setFailed = () => {},
  organizationCode,
}) => {
  const { integrationService } = useContainer();
  const currentUser = useCurrentUser();
  const { organizationCode: userOrgCode } = currentUser || {};
  const effectiveOrgCode = organizationCode || userOrgCode;
  const cacheKey = getCacheKey(effectiveOrgCode, connectionId, jobId);
  const { showError } = useNotifications();

  const [showSyncErrorsModal, setShowSyncErrorsModal] = useState(false);

  // Fetch sync errors with caching to avoid redundant API calls
  // Errors are cached by connectionId + jobId combination
  const {
    loading: syncErrorsLoading,
    response: syncErrors = [],
    sendRequest: requestSyncErrors,
  } = useApiRequest(async () => {
    try {
      // Check cache first
      const cached = getCachedSyncErrors(cacheKey);
      if (cached) {
        return cached;
      }

      // Fetch from API and cache the result
      const result = await integrationService.getSyncErrors(
        connectionId,
        jobId,
        effectiveOrgCode
      );
      setCachedSyncErrors(cacheKey, result);
      return result;
    } catch {
      showError(
        `An error ocurred while fetching sync errors for your connection.`
      );
    }
  }, false);

  // Fetch sync errors when connectionId or jobId changes
  useEffect(() => {
    if (connectionId && jobId) {
      requestSyncErrors();
    }
  }, [connectionId, jobId, effectiveOrgCode]);

  const errorBadge = (
    <div onClick={() => setShowSyncErrorsModal(true)}>
      <Badge
        value={
          <>
            <FontAwesomeIcon icon={["fa-solid", "triangle-exclamation"]} />
            View Errors
          </>
        }
        className="badge error"
      />
    </div>
  );

  const successBadge = (
    <Badge
      value={
        <>
          <FontAwesomeIcon icon={["fa-solid", "circle-check"]} />
          Success
        </>
      }
      className="badge success"
    />
  );

  const loadingSkeleton = (
    <div
      className="badge skeleton"
      style={{
        display: "inline-block",
        width: 96,
        height: 20,
        borderRadius: 4,
        background: "linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%)",
        backgroundSize: "400% 100%",
        animation: "syncBadgeSkeleton 1.4s ease infinite",
      }}
    />
  );

  // Calculate error state based on run status and sync errors
  const errorState = useMemo(() => {
    const errorsExist = syncErrors && syncErrors.length > 0;
    // Error state: either run failed OR run succeeded but has errors
    return runStatus === "Failed" || (runStatus === "Success" && errorsExist);
  }, [runStatus, syncErrors]);

  // Determine which badge to display based on run status and sync errors
  const syncInfo = useMemo(() => {
    // Show loading skeleton while fetching errors
    if (syncErrorsLoading) {
      return loadingSkeleton;
    }

    if (errorState) {
      return errorBadge;
    }

    // Success state: run succeeded AND no errors exist
    const isInSuccessState = runStatus === "Success" && !syncErrorsLoading;
    if (isInSuccessState) {
      return successBadge;
    }
  }, [
    syncErrorsLoading,
    errorState,
    runStatus,
    errorBadge,
    successBadge,
    loadingSkeleton,
  ]);

  useEffect(() => {
    // Don't update failed state while still loading sync errors
    if (syncErrorsLoading) {
      return;
    }

    // Update parent component with current error state
    setFailed(errorState);
  }, [setFailed, errorState, syncErrorsLoading]);

  return (
    <div className="sync-badge">
      {syncInfo}
      <SyncIntegrationErrorsModal
        open={showSyncErrorsModal}
        onClose={() => setShowSyncErrorsModal(false)}
        errors={syncErrors}
      />
    </div>
  );
};

export default SyncBadge;
